import { Dialog, useMediaQuery } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useForm, Control, FieldValues } from 'react-hook-form'
import { useTheme } from '@mui/material/styles'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { PromoCodesCode } from './PromoCodesDialogFields/PromoCodesCode'
import { SectionTitle } from '../../../components/FormCommons'
import { PromoCodesValidTo } from './PromoCodesDialogFields/PromoCodesValidTo'
import { PromoCodesUsesAvailable } from './PromoCodesDialogFields/PromoCodesUsesAvailable'
import { PromoCodesPromoPrice } from './PromoCodesDialogFields/PromoCodesPromoPrice'
import { PromoCodesButtons } from './PromoCodesDialogFields/PromoCodesButtons'
import { PromoCodesProductId } from './PromoCodesDialogFields/PromoCodesProductId'

const validationSchema = yup.object().shape({
  code: yup.string().required('Kod jest wymagany.'),
  valid_to: yup.date().typeError('Podaj prawidłową datę.'),
  unlimited: yup.boolean(),
  uses_available: yup
    .number()
    .when('unlimited', {
      is: false,
      then: schema =>
        schema
          .min(1, 'Liczba użyć musi być większa od 0.')
          .required('Liczba użyć jest wymagana.'),
      otherwise: schema => schema.nullable().transform((_, val) => val || null)
    })
    .typeError('Podaj prawidłową liczbę.'),
  percentage_value: yup
    .number()
    .nullable()
    .transform((_, val) => val || null),
  price_value: yup
    .number()
    .nullable()
    .transform((_, val) => val || null),
  currency: yup.string().when('price_value', {
    is: (val) => val != null && val > 0,
    then: schema => schema.required('Waluta jest wymagana przy zniżce kwotowej.'),
    otherwise: schema => schema.optional().nullable()
  }),
  product_id: yup.string(),
  id: yup.string().optional()
}).test(
  'price-or-percentage-required',
  'Należy podać zniżkę procentową lub kwotową (ale nie obie).',
  value => !!value.percentage_value !== !!value.price_value
)

export const SuperAdminPromoCodesDialog = ({
  open,
  promoCodeData,
  onClose,
  headerTitle,
  onSubmit
}: {
  open: boolean
  promoCodeData?: any
  onClose: () => void
  headerTitle: string
  onSubmit: (data: any) => void
}) => {
  const theme = useTheme()
  const md = useMediaQuery(theme.breakpoints.up('md'))
  const [isSaving, setIsSaving] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      id: promoCodeData?.id || '',
      code: promoCodeData?.code || '',
      valid_to: promoCodeData?.valid_to || '',
      uses_available: promoCodeData?.uses_available ?? '',
      unlimited: promoCodeData?.uses_available === -1,
      percentage_value: promoCodeData?.percentage_value || '',
      price_value: promoCodeData?.price_value || '',
      currency: promoCodeData?.currency || '',
      product_id: promoCodeData?.product_id || ''
    }
  })

  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
    reset
  } = formMethods

  useEffect(() => {
    reset({
      id: promoCodeData?.id || '',
      code: promoCodeData?.code || '',
      valid_to: promoCodeData?.valid_to || '',
      uses_available: promoCodeData?.uses_available ?? '',
      unlimited: promoCodeData?.uses_available === -1,
      percentage_value: promoCodeData?.percentage_value || '',
      price_value: promoCodeData?.price_value || '',
      currency: promoCodeData?.currency || '',
      product_id: promoCodeData?.product_id || ''
    })
  }, [promoCodeData, reset])

  const onFormSubmit = async (data: any) => {
    if (isSaving) {
      return
    }
    try {
      setIsSaving(true)
      await onSubmit(data)
      enqueueSnackbar(t('ui.trips.success_save', 'Zapisano!'), {
        variant: 'success'
      })
      setIsSaving(false)
      onClose()
    } catch (err) {
      setIsSaving(false)
      console.error('Error saving')
      enqueueSnackbar(
        t('ui.trips.error_save', 'Problem z zapisem ') + (err as any)?.message,
        {
          variant: 'error'
        }
      )
    }
  }

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          maxWidth: md ? '564px' : '300px',
          width: '100%',
          borderRadius: '8px'
        }
      }}
      onClose={onClose}
    >
      <div className='flex flex-col gap-4 pt-6 pb-10 px-10'>
        <div>
          <div className='flex justify-end cursor-pointer' onClick={onClose}>
            <Close width={16} />
          </div>
          <section>
            <SectionTitle useBlueBackground={false}>
              {headerTitle}
            </SectionTitle>
            <form onSubmit={handleSubmit(onFormSubmit)} className='p-2'>
              <PromoCodesCode
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
                promoCode={promoCodeData?.code}
              />
              <PromoCodesValidTo
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
                validTo={promoCodeData?.valid_to}
              />
              <PromoCodesUsesAvailable
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
              />
              <PromoCodesPromoPrice
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
                percentageValue={promoCodeData?.percentage_value}
                priceValue={promoCodeData?.price_value}
                currency={promoCodeData?.currency}
              />
              <PromoCodesProductId
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
                product_id={promoCodeData?.product_id}
              />
              <PromoCodesButtons
                isSaving={isSaving}
                isValid={isValid}
                onClose={onClose}
              />
            </form>
          </section>
        </div>
      </div>
    </Dialog>
  )
}
